import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PropertyCard from "../components/PropertyCard";
import { fetchMLSAll } from "../services/propertyService";
import { Search, SlidersHorizontal, X, Bed, Bath, Hash, Loader2, ExternalLink } from "lucide-react";

export default function MLS() {
  const [activeTab, setActiveTab] = useState("all");
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [priceRange, setPriceRange] = useState({ min: 0, max: 20000000 });
  const [beds, setBeds] = useState("any");
  const [baths, setBaths] = useState("any");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [displayCount, setDisplayCount] = useState(12);

  const normalizeProperty = (p) => {
    const rawPrice = Number(p.CurrentPrice || p.ListPrice || p.Price || 0);
    return {
      id: p.card_id || p.mls_number || String(p.id),
      title: p.Name || p.title || "St. John MLS Listing",
      price: p.ui_price || (rawPrice ? `$${rawPrice.toLocaleString()}` : "$0"),
      rawPrice: rawPrice,
      location: p.address_line1 ? `${p.address_line1}, ${p.City || "St. John"}` : (p.City || "St. John, USVI"),
      image: p.images?.[0] || p.card_image || "https://images.unsplash.com/photo-1512917774080-9991f1c4c750",
      beds: p.BedsTotal || p.StandardFields?.BedsTotal || p.beds || "0",
      baths: p.BathsTotal || p.StandardFields?.BathsTotal || p.baths || "0",
      sqft: p.LivingArea || p.BuildingAreaTotal || p.StandardFields?.LivingArea || (p.lot_size_acres ? `${p.lot_size_acres} Acres` : "N/A"),
      type: p.PropertyType || p.StandardFields?.PropertyType || "Residential",
      status: p.MlsStatus || p.status || "Active",
      slug: p.card_id || String(p.id),
      externalLink: p.href || `https://340realestate.com/mls`,
      source: "mls"
    };
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const mlsRaw = await fetchMLSAll();
        // Filter out commercial and closed/sold listings
        const filteredRaw = (mlsRaw || []).filter((p) => {
          const type = (p.PropertyType || p.StandardFields?.PropertyType || p.type || "").toLowerCase();
          const status = (p.MlsStatus || p.status || "").toLowerCase();
          const listType = (p.listing_type || "").toLowerCase();

          const isCommercial = type.includes("commercial") || type.includes("comm");
          const isSold = status === "closed" || status === "sold" || listType === "sold";

          return !isCommercial && !isSold;
        });

        const normalized = filteredRaw.map(normalizeProperty);
        setProperties(normalized);
        setFilteredProperties(normalized);
      } catch (error) {
        console.error("Failed to load MLS properties:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    let result = properties;

    // Tab Filtering
    if (activeTab === "residential") {
      result = result.filter((p) => {
        const type = p.type?.toLowerCase() || "";
        return type.includes("residential") || type.includes("res") || type.includes("condo") || type.includes("house");
      });
    } else if (activeTab === "land") {
      result = result.filter((p) => {
        const type = p.type?.toLowerCase() || "";
        return type.includes("land");
      });
    }

    // Search Query
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.title?.toLowerCase().includes(q) ||
          p.location?.toLowerCase().includes(q) ||
          p.id?.toLowerCase().includes(q)
      );
    }

    // Price Filter
    if (priceRange.min > 0) {
      result = result.filter((p) => p.rawPrice >= priceRange.min);
    }
    if (priceRange.max < 20000000) {
      result = result.filter((p) => p.rawPrice <= priceRange.max);
    }

    // Beds Filter
    if (beds !== "any") {
      result = result.filter((p) => Number(p.beds) >= Number(beds));
    }

    // Baths Filter
    if (baths !== "any") {
      result = result.filter((p) => Number(p.baths) >= (Number(baths) || 0));
    }

    setFilteredProperties(result);
  }, [activeTab, searchQuery, priceRange, beds, baths, properties]);

  const tabs = [
    { id: "all", label: "All MLS Active" },
    { id: "residential", label: "Residential" },
    { id: "land", label: "Land" }
  ];

  return (
    <div className="bg-[#FAF6F0] min-h-screen font-sans flex flex-col">
      <Navbar isTransparent={false} />

      {/* Header Banner */}
      <section className="pt-24 lg:pt-32 pb-16 bg-ink-900 text-white relative overflow-hidden px-6 md:px-12">
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-4 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20"
          >
            <span className="w-2 h-2 rounded-full bg-tropical-400 animate-pulse"></span>
            <span className="text-xs font-semibold text-white tracking-widest uppercase font-display flex items-center gap-1.5">
              Island-wide MLS Database <ExternalLink className="w-3 h-3 text-tropical-400" />
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-tight font-display mb-6"
          >
            Global MLS Search & <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-tropical-300 to-white/70">
              St. John Listings.
            </span>
          </motion.h1>
          <p className="text-ink-400 text-base md:text-lg max-w-2xl font-light">
            Search real-time Multiple Listing Service (MLS) properties across St. John and the U.S. Virgin Islands. Click any listing to view detailed MLS records.
          </p>
        </div>
      </section>

      {/* Sticky Tab & Search Filter Bar */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-[#E8E3DC] shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col md:flex-row justify-between items-center gap-4">
          
          {/* Tabs */}
          <div className="flex gap-2 sm:gap-6 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setDisplayCount(12);
                }}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-full transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? "bg-brand-dark text-white shadow-sm"
                    : "text-ink-600 hover:bg-gray-100 hover:text-ink-900"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search Box & Filter Button */}
          <div className="flex items-center gap-4 w-full md:w-auto">
            <div className="relative flex-grow md:w-72">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
              <input
                type="text"
                placeholder="Search MLS#, location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 bg-[#FAF8F5] border border-[#E8E3DC] rounded-xl text-xs text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-tropical-500/20 focus:border-tropical-500 transition-all font-light"
              />
            </div>

            <button
              onClick={() => setIsFilterOpen(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-brand-dark hover:bg-brand-dark/95 text-white rounded-xl text-xs font-bold uppercase tracking-wider shadow-sm transition-all shrink-0 cursor-pointer"
            >
              <SlidersHorizontal className="w-4 h-4" /> Filters
            </button>
          </div>
        </div>
      </div>

      {/* Main MLS Property Grid */}
      <section className="py-16 px-6 md:px-12 flex-grow">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="h-96 flex flex-col items-center justify-center gap-4">
              <Loader2 className="w-10 h-10 text-tropical-500 animate-spin" />
              <p className="text-sm font-medium text-ink-600 font-display">Fetching live MLS records...</p>
            </div>
          ) : filteredProperties.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                {filteredProperties.slice(0, displayCount).map((prop) => (
                  <PropertyCard key={prop.id} {...prop} />
                ))}
              </div>

              {filteredProperties.length > displayCount && (
                <div className="text-center mt-12 mb-8">
                  <button
                    onClick={() => setDisplayCount((prev) => prev + 12)}
                    className="px-8 py-4 bg-brand-dark hover:bg-brand-dark/95 text-white text-xs font-bold uppercase tracking-widest rounded-xl transition-all shadow-md cursor-pointer font-sans"
                  >
                    Load More MLS Listings ({filteredProperties.length - displayCount} remaining)
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-24 bg-white rounded-3xl border border-[#E8E3DC] shadow-sm max-w-2xl mx-auto px-6">
              <div className="w-16 h-16 bg-[#FAF8F5] rounded-full flex items-center justify-center mx-auto mb-6 border border-[#E8E3DC]">
                <Search className="w-6 h-6 text-ink-400" />
              </div>
              <h3 className="text-2xl font-bold text-ink-900 font-display mb-2">
                No MLS listings found
              </h3>
              <p className="text-ink-600 text-sm font-light leading-relaxed mb-6">
                We couldn't find any MLS properties matching your search criteria. Try adjusting your query or price filters.
              </p>
              <button
                onClick={() => {
                  setSearchQuery("");
                  setActiveTab("all");
                  setPriceRange({ min: 0, max: 20000000 });
                  setBeds("any");
                  setBaths("any");
                }}
                className="px-6 py-3 bg-brand-dark text-white text-xs font-bold uppercase tracking-wider rounded-xl hover:bg-brand-dark/95 transition-all shadow-md cursor-pointer"
              >
                Reset All Filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Filter Drawer */}
      <AnimatePresence>
        {isFilterOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsFilterOpen(false)}
              className="fixed inset-0 bg-ink-900/60 backdrop-blur-sm z-[100]"
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-[110] p-8 shadow-2xl overflow-y-auto font-sans"
            >
              <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-100">
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-widest text-tropical-500 font-display">
                    MLS Search Filters
                  </h4>
                  <h2 className="text-2xl font-bold text-ink-900 font-display">Filter MLS Listings</h2>
                </div>
                <button
                  onClick={() => setIsFilterOpen(false)}
                  className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-ink-600 hover:bg-ink-900 hover:text-white transition-all cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-8">
                {/* Price Range */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Hash className="w-4 h-4 text-tropical-500" />
                    <h4 className="text-xs font-bold uppercase tracking-wider text-ink-900">
                      Price Budget ($)
                    </h4>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] uppercase font-bold text-ink-500 mb-1 block">Min Price</label>
                      <input
                        type="number"
                        placeholder="0"
                        value={priceRange.min}
                        onChange={(e) => setPriceRange((prev) => ({ ...prev, min: Number(e.target.value) }))}
                        className="w-full px-4 py-3 bg-[#FAF8F5] border border-[#E8E3DC] rounded-xl text-sm text-ink-900 focus:outline-none focus:border-tropical-500 font-light"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase font-bold text-ink-500 mb-1 block">Max Price</label>
                      <input
                        type="number"
                        placeholder="20000000"
                        value={priceRange.max}
                        onChange={(e) => setPriceRange((prev) => ({ ...prev, max: Number(e.target.value) }))}
                        className="w-full px-4 py-3 bg-[#FAF8F5] border border-[#E8E3DC] rounded-xl text-sm text-ink-900 focus:outline-none focus:border-tropical-500 font-light"
                      />
                    </div>
                  </div>
                </div>

                {/* Min Bedrooms */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Bed className="w-4 h-4 text-tropical-500" />
                    <h4 className="text-xs font-bold uppercase tracking-wider text-ink-900">
                      Minimum Bedrooms
                    </h4>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {["any", "1", "2", "3", "4", "5+"].map((num) => (
                      <button
                        key={num}
                        onClick={() => setBeds(num)}
                        className={`px-4 py-2.5 text-xs font-bold rounded-xl transition-all border cursor-pointer ${
                          beds === num
                            ? "bg-brand-dark text-white border-brand-dark shadow-sm"
                            : "bg-[#FAF8F5] text-ink-600 border-[#E8E3DC] hover:border-tropical-500"
                        }`}
                      >
                        {num.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Min Bathrooms */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Bath className="w-4 h-4 text-tropical-500" />
                    <h4 className="text-xs font-bold uppercase tracking-wider text-ink-900">
                      Minimum Bathrooms
                    </h4>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {["any", "1", "2", "3", "4+"].map((num) => (
                      <button
                        key={num}
                        onClick={() => setBaths(num)}
                        className={`px-4 py-2.5 text-xs font-bold rounded-xl transition-all border cursor-pointer ${
                          baths === num
                            ? "bg-brand-dark text-white border-brand-dark shadow-sm"
                            : "bg-[#FAF8F5] text-ink-600 border-[#E8E3DC] hover:border-tropical-500"
                        }`}
                      >
                        {num.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-6 border-t border-gray-100 flex flex-col gap-3">
                <button
                  onClick={() => setIsFilterOpen(false)}
                  className="w-full py-4 bg-brand-dark hover:bg-brand-dark/95 text-white text-xs font-bold uppercase tracking-widest rounded-xl transition-all shadow-md cursor-pointer"
                >
                  Show {filteredProperties.length} Results
                </button>
                <button
                  onClick={() => {
                    setBeds("any");
                    setBaths("any");
                    setPriceRange({ min: 0, max: 20000000 });
                  }}
                  className="w-full py-2.5 text-xs font-bold uppercase tracking-wider text-ink-500 hover:text-ink-900 transition-colors cursor-pointer"
                >
                  Reset Defaults
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
